import { useState, useEffect } from "react";
import { ActionMenu, ActionList, Button, Text, Stack, FormControl } from "@primer/react";
import { ChevronLeftIcon } from '@primer/octicons-react';
import { DateRangeInput } from "../../basic/inputs/DateRangeInput";
import { TimeRangeInput } from "../../basic/inputs/TimeInput";

interface DateTimeFormModalProps {
    dateFilterOpened: boolean;
    setDateFilterOpened: (value: boolean) => void;
    fromDate: string;
    setFromDate: (value: string) => void;
    toDate: string;
    setToDate: (value: string) => void;
    fromTime: string;
    setFromTime: (value: string) => void;
    toTime: string;
    setToTime: (value: string) => void;
    dateFormError: boolean;
    onDismiss: () => void;
    onApply: () => void;
    isShowBackButton?: boolean;
}

const DateTimeFormModal: React.FC<DateTimeFormModalProps> = ({
    dateFilterOpened,
    setDateFilterOpened,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    fromTime,
    setFromTime,
    toTime,
    setToTime,
    dateFormError,
    onDismiss,
    onApply,
    isShowBackButton = true,
}) => {
    const [initialValues, setInitialValues] = useState({
        fromDate: "",
        toDate: "",
        fromTime: "",
        toTime: "",
    });

    useEffect(() => {
        if (dateFilterOpened) {
            setInitialValues({ fromDate, toDate, fromTime, toTime });
        }
    }, [dateFilterOpened, fromDate, toDate, fromTime, toTime]);

    const dismiss = () => {
        // Восстановить значения при выходе назад
        if (dateFormError) {
            setFromDate('');
            setToDate('');
            setFromTime('');
            setToTime('');
        } else {
            setFromDate(initialValues.fromDate);
            setToDate(initialValues.toDate);
            setFromTime(initialValues.fromTime);
            setToTime(initialValues.toTime);
        }

        onDismiss();
    };

    const clearForm = () => {
        setFromDate("");
        setToDate("");
        setFromTime("");
        setToTime("");
    };

    const resetForm = () => {
        clearForm();
        setInitialValues({ fromDate: "", toDate: "", fromTime: "", toTime: "" });
    };

    const canClear = Boolean(
        (fromDate && fromDate.trim()) ||
        (toDate && toDate.trim()) ||
        (fromTime && fromTime.trim()) ||
        (toTime && toTime.trim())
    );

    return (
        <ActionMenu open={dateFilterOpened} onOpenChange={setDateFilterOpened}>
            <ActionMenu.Button className="w-8 h-8 right-[16px] absolute ml-auto invisible"></ActionMenu.Button>
            <ActionMenu.Overlay className="!w-[320px]">
                <ActionList>
                    <Stack justify="space-between" direction="horizontal">
                        <Stack direction="horizontal">
                            {isShowBackButton ? (
                                <Button leadingVisual={ChevronLeftIcon} variant="invisible" onClick={dismiss}>
                                    <Text className="block mb-[1.5px]">По дате и времени</Text>
                                </Button>
                            ) : (
                                <Text className="block mb-[8px] ml-4">По дате и времени</Text>
                            )}
                        </Stack>
                    </Stack>

                    <Stack className="ml-4 mr-4">
                        <DateRangeInput
                            fromDate={fromDate}
                            toDate={toDate}
                            onFromDateChange={setFromDate}
                            onToDateChange={setToDate}
                            error={dateFormError}
                        />

                        <TimeRangeInput
                            fromTime={fromTime}
                            toTime={toTime}
                            onFromTimeChange={setFromTime}
                            onToTimeChange={setToTime}
                            error={dateFormError}
                        />

                        {dateFormError && (
                            <FormControl.Validation variant="error" className="mt-[1px]">
                                Вы ввели некорректные значения ("От" должно быть строго меньше "До")
                            </FormControl.Validation>
                        )}
                    </Stack>

                    <Stack direction="horizontal" gap="normal" justify="end" className="mt-[22px] mr-4">
                        <div />
                        <Button disabled={!canClear} onClick={resetForm} size="small" variant="default">
                            Очистить
                        </Button>
                        <Button onClick={onApply} size="small" variant="primary" disabled={dateFormError}>
                            Применить
                        </Button>
                    </Stack>
                </ActionList>
            </ActionMenu.Overlay>
        </ActionMenu>
    );
};

const formatTimeRange = (fromTime: string, toTime: string): string => {
    const formatTime = (timeString: string): string => {
        if (!timeString) return '';
        const [hours, minutes] = timeString.split(':');
        return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    };

    const formattedFrom = formatTime(fromTime);
    const formattedTo = formatTime(toTime);

    if (formattedFrom && formattedTo) return `${formattedFrom}-${formattedTo}`;
    if (formattedFrom) return `от ${formattedFrom}`;
    if (formattedTo) return `до ${formattedTo}`;
    return '';
};

export { DateTimeFormModal, formatTimeRange };
